import React, { useEffect, useMemo, useState } from 'react';
import StepProgress from './StepProgress';
import { useProgress } from '../contexts/ProgressContext';

const TutorialModule = ({ tutorial }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  
  const { updateTutorialProgress, getProgress } = useProgress();

  // Load saved tutorial progress to resume current step (only on mount)
  useEffect(() => {
    try {
      const saved = getProgress('tutorials', tutorial.id);
      if (saved) {
        if (typeof saved.currentStep === 'number') {
          const clamped = Math.min(Math.max(saved.currentStep, 0), tutorial.steps.length - 1);
          setCurrentStep(clamped);
          // Check if this step was already completed
          if (saved.completedSteps && saved.completedSteps.includes(clamped)) {
            setIsCompleted(true);
          }
        }
      }
    } catch (_) {
      // no-op if progress context not ready yet
    }
  // Only run on mount and when tutorial changes, not when progress updates
  }, [tutorial.id, tutorial.steps.length, getProgress]);

  const totalSteps = Array.isArray(tutorial.steps) ? tutorial.steps.length : 0;
  const clampedStepIndex = useMemo(() => {
    if (totalSteps === 0) return 0;
    return Math.min(Math.max(currentStep, 0), totalSteps - 1);
  }, [currentStep, totalSteps]);

  const currentStepData = totalSteps > 0 ? tutorial.steps[clampedStepIndex] : null;

  // Check completion state when step changes
  useEffect(() => {
    setIsCompleted(false);
    
    // Check if this step was already completed
    try {
      const saved = getProgress('tutorials', tutorial.id);
      if (saved && saved.completedSteps && saved.completedSteps.includes(clampedStepIndex)) {
        setIsCompleted(true);
      }
    } catch (_) {
      // no-op
    }
  }, [clampedStepIndex, tutorial.id, getProgress]);

  const renderStepContent = (content) => {
    const lines = String(content || '').split('\n');
    const elements = [];
    let inCodeBlock = false;
    let codeBuffer = [];

    const flushCode = (key) => {
      if (codeBuffer.length > 0) {
        elements.push(
          <pre key={key} className="code-block"><code>{codeBuffer.join('\n')}</code></pre>
        );
        codeBuffer = [];
      }
    };

    const processLine = (line, index) => {
      const trimmed = line.trim();
      
      // Check for markdown headings
      if (trimmed.startsWith('#')) {
        const match = trimmed.match(/^(#{1,6})\s+(.+)$/);
        if (match) {
          const level = match[1].length;
          const text = match[2];
         
          const headingLevel = Math.min(level + 2, 6);
          const HeadingComponent = {
            3: (props) => <h3 {...props} className="step-heading step-heading-h3">{props.children}</h3>,
            4: (props) => <h4 {...props} className="step-heading step-heading-h4">{props.children}</h4>,
            5: (props) => <h5 {...props} className="step-heading step-heading-h5">{props.children}</h5>,
            6: (props) => <h6 {...props} className="step-heading step-heading-h6">{props.children}</h6>
          }[headingLevel] || ((props) => <h3 {...props} className="step-heading step-heading-h3">{props.children}</h3>);
          
          return <HeadingComponent key={`heading-${index}`}>{text}</HeadingComponent>;
        }
      }
      
      // Check for bold text (**text**)
      if (trimmed.includes('**')) {
        const parts = trimmed.split(/(\*\*[^*]+\*\*)/g);
        const processedParts = parts.map((part, partIndex) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={`strong-${index}-${partIndex}`}>{part.slice(2, -2)}</strong>;
          }
          return part;
        });
        return <p key={`p-${index}`}>{processedParts}</p>;
      }
      
      // Regular paragraph
      return <p key={`p-${index}`}>{line}</p>;
    };

    lines.forEach((line, index) => {
      const isFence = line.trim().startsWith('```');
      if (isFence) {
        if (inCodeBlock) {
          // closing fence
          flushCode(`code-${index}`);
          inCodeBlock = false;
        } else {
          // opening fence
          inCodeBlock = true;
        }
        return; // do not render fence line
      }

      if (inCodeBlock) {
        codeBuffer.push(line);
        return;
      }

      if (line.trim() === '') {
        elements.push(<br key={`br-${index}`} />);
        return;
      }

      elements.push(processLine(line, index));
    });

    // If file ends while in a code block, flush what we have
    if (inCodeBlock) {
      flushCode('code-final');
    }

    return elements;
  };

  const markStepComplete = () => {
    setIsCompleted(true);
    updateTutorialProgress(tutorial.id, currentStep, true);
  };

  const nextStep = () => {
    if (totalSteps === 0) return;
    setCurrentStep((prev) => {
      const next = Math.min(prev + 1, totalSteps - 1);
      if (next !== prev) {
        // Persist that previous step was reached
        updateTutorialProgress(tutorial.id, prev, true);
      }
      return next;
    });
    setIsCompleted(false);
  };

  const prevStep = () => {
    if (totalSteps === 0) return;
    setCurrentStep((prev) => Math.max(prev - 1, 0));
    setIsCompleted(false);
  };

  if (totalSteps === 0 || !currentStepData) {
    return (
      <div className="tutorial-module">
        <div className="tutorial-header">
          <h1 className="tutorial-title">{tutorial.title}</h1>
          <p className="tutorial-description">{tutorial.description}</p>
        </div>
        <div className="tutorial-content">No steps available.</div>
      </div>
    );
  }

  return (
    <div className="tutorial-module">
      <div className="tutorial-header">
        <h1 className="tutorial-title">{tutorial.title}</h1>
        <p className="tutorial-description">{tutorial.description}</p>
        <StepProgress currentIndex={clampedStepIndex} total={totalSteps} labelPrefix="Step" />
      </div>

      <div className="tutorial-content">
        <div className="step-content">
          <h3 className="step-title">{currentStepData.title}</h3>
          <div className="step-explanation">{renderStepContent(currentStepData.content)}</div>
          
          {/* Visual diagram if available */}
          {currentStepData.visual && (
            <div className="step-visual" style={{ 
              marginTop: '24px', 
              padding: '20px', 
              background: 'rgba(15, 23, 42, 0.6)',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              {currentStepData.visual}
            </div>
          )}
        </div>

        {/* CTA to official Playground for code examples */}
        {currentStepData.code && (
          <div className="playground-cta" style={{
            marginTop: '32px',
            padding: '20px',
            background: 'rgba(249, 115, 22, 0.1)',
            border: '1px solid rgba(249, 115, 22, 0.3)',
            borderRadius: '8px'
          }}>
            <h4 style={{ color: '#f97316', marginBottom: '12px', fontSize: '16px' }}>
              Want to see this in real Miden Assembly?
            </h4>
            <p style={{ color: '#94a3b8', marginBottom: '16px', fontSize: '14px', lineHeight: '1.6' }}>
              Open the official Miden Playground to write and execute this code. You'll see how these concepts work in practice.
            </p>
            <div style={{ 
              background: 'rgba(15, 23, 42, 0.8)', 
              padding: '12px', 
              borderRadius: '6px',
              marginBottom: '16px',
              fontFamily: 'monospace',
              fontSize: '12px',
              color: '#cbd5e1',
              overflowX: 'auto'
            }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{currentStepData.code}</pre>
            </div>
            <a
              href="https://playground.miden.xyz"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                padding: '10px 20px',
                background: 'rgba(249, 115, 22, 0.15)',
                border: '1px solid rgba(249, 115, 22, 0.4)',
                borderRadius: '6px',
                color: '#f97316',
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '14px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(249, 115, 22, 0.2)';
                e.target.style.borderColor = 'rgba(249, 115, 22, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(249, 115, 22, 0.15)';
                e.target.style.borderColor = 'rgba(249, 115, 22, 0.4)';
              }}
            >
              Open Official Playground →
            </a>
          </div>
        )}

        <div className="step-navigation" style={{ marginTop: '32px' }}>
          <button 
            className="nav-button prev"
            onClick={prevStep}
            disabled={currentStep === 0}
          >
            ← Previous
          </button>
          <button 
            className="nav-button complete"
            onClick={markStepComplete}
            style={{ 
              margin: '0 8px',
              padding: '10px 20px',
              background: isCompleted ? 'rgba(16, 185, 129, 0.15)' : 'rgba(100, 116, 139, 0.1)',
              border: isCompleted ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(100, 116, 139, 0.3)',
              borderRadius: '6px',
              color: isCompleted ? '#10b981' : '#94a3b8',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {isCompleted ? '✓ Completed' : 'Mark Complete'}
          </button>
          <button 
            className="nav-button next"
            onClick={nextStep}
            disabled={clampedStepIndex === totalSteps - 1}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
};

export default TutorialModule;
